// Copyright 2017 Sorama B.V.

import { BindingEngine, computedFrom, Disposable, inject } from "aurelia-framework";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { MeasurementSetupRepository } from "repositories/MeasurementSetupRepository";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import {
    ConnectedMeasurementDeviceSetup,
    MeasurementDeviceSetupStub,
    MeasurementDeviceStub,
} from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { NotificationUrgency } from "services/Notification/INotificationService";

@inject(
    MeasurementSetupRepository,
    "IHardwareService<MeasurementDevice>",
    IMeasurementAcquisitionService,
    IChannelSelectionPresetService,
    BindingEngine
)
export class MeasurementSetupModule implements IWorkflowModuleLifetime {
    //#region Workflow Properties
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedMeasurementSetupId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedMeasurementDeviceSetupInfoId: string;
    //#endregion

    //#region UI Properties
    private selectedFrontEndId: string;

    private measurementDeviceSetup: MeasurementDeviceSetupStub;
    private moduleInstance: IWorkflowModuleInstance;

    @computedFrom("measurementDeviceService.hardware.length", "measurementDeviceSetup")
    private get selectableMeasurementDevices(): MeasurementDevice[] {
        if (!this.measurementDeviceSetup) return [];

        const deviceInSetupIds = this.measurementDeviceSetup.measurementDevices.map((x) => x.id);

        return this.measurementDeviceService.hardware.filter(
            (availableDevice) => !deviceInSetupIds.some((deviceInSetupId) => availableDevice.id === deviceInSetupId)
        );
    }

    @computedFrom("measurementDeviceSetup")
    private get setupNotes(): string {
        return this.measurementDeviceSetup ? this.measurementDeviceSetup.notes : "";
    }

    private set setupNotes(value: string) {
        this.measurementDeviceSetup.notes = value;
        this.updateMeasurementSetup();
    }

    @computedFrom("measurementDeviceSetup")
    private get hasEmptyDevice(): boolean {
        return this.measurementDeviceSetup && this.measurementDeviceSetup.measurementDevices
            ? this.measurementDeviceSetup.measurementDevices.some((x) => !x.id)
            : false;
    }
    //#endregion

    //#region Private Fields
    private subscriptions: Disposable[];
    //#endregion

    //#region Constructor and Initialization
    constructor(
        private readonly measurementSetupRepository: MeasurementSetupRepository,
        private readonly measurementDeviceService: IHardwareService<MeasurementDevice>,
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly channelSelectionPresetService: IChannelSelectionPresetService,
        private readonly bindingEngine: BindingEngine
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.subscriptions = [
            this.bindingEngine
                .collectionObserver(this.measurementSetupRepository.items)
                .subscribe(() => this.selectedMeasurementSetupIdChanged()),
        ];

        this.selectedMeasurementSetupIdChanged();
    }

    public deactivateModule(): void {
        while (this.subscriptions.length != 0) {
            this.subscriptions.pop().dispose();
        }
    }
    //#endregion

    //#region Property Changed Callbacks
    public selectedMeasurementSetupIdChanged(): void {
        if (!this.selectedMeasurementSetupId) return;

        this.measurementSetupRepository
            .getById(this.selectedMeasurementSetupId)
            .then((setup) => {
                this.measurementDeviceSetup = setup;
            })
            .catch((error: Error) => {
                this.selectedMeasurementSetupId = null;
            });
    }

    //#endregion

    //#region User Input
    public updateMeasurementSetupName(event: CustomEvent): void {
        this.measurementDeviceSetup.name = event.detail.newValue;
        this.updateMeasurementSetup();
    }

    public addDevice(): void {
        if (
            this.measurementAcquisitionService.activeSetup &&
            this.measurementAcquisitionService.activeSetup.id === this.measurementDeviceSetup.id
        ) {
            this.moduleInstance
                .createNotification()
                .withMessage("Adding a device will disconnect the current setup.")
                .withButton("Disconnect and Add Device", () => {
                    this.measurementAcquisitionService.setActiveSetup(null);
                    this._addDevice();
                })
                .withButton("Don't Add Device")
                .lockModule()
                .show();
        } else {
            this._addDevice();
        }
    }

    private _addDevice(): void {
        if (!this.selectedFrontEndId) return;

        this.measurementDeviceService.getHardwareById(this.selectedFrontEndId).then((measurementDevice) => {
            this.selectedFrontEndId = undefined;

            // Get default channel selection
            const channelSelection = this.channelSelectionPresetService.getDefaultPreset(measurementDevice.frontEnd);

            // Add new device to measurement setup
            const newDeviceSetup = MeasurementDeviceStub.fromMeasurementDevice(measurementDevice, channelSelection);
            this.measurementDeviceSetup.measurementDevices.push(newDeviceSetup);

            this.updateMeasurementSetup().then(() => {
                this.selectedMeasurementDeviceSetupInfoId = newDeviceSetup.id;
            });
        });
    }

    public deviceDeleted(event: CustomEvent): void {
        if (
            this.measurementAcquisitionService.activeSetup &&
            this.measurementAcquisitionService.activeSetup.id === this.measurementDeviceSetup.id
        ) {
            // Load device setup
            ConnectedMeasurementDeviceSetup.fromConnectedDevices(
                this.measurementDeviceSetup,
                this.measurementDeviceService
            )
                .then((setup) => {
                    this.measurementAcquisitionService.setActiveSetup(setup);
                })
                .catch((e) => {
                    console.error(e);
                    this.measurementAcquisitionService.setActiveSetup(null);
                });
        }
    }
    //#endregion

    //#region Helper Methods
    private updateMeasurementSetup(): Promise<void> {
        // Store in db
        return this.measurementSetupRepository
            .put(this.measurementDeviceSetup)
            .then(() => {})
            .catch((e: Error) => {
                this.moduleInstance
                    .createNotification()
                    .withUrgency(NotificationUrgency.Error)
                    .withMessage(e.message)
                    .show();
            });
    }
    //#endregion
}
